/**
 * Photos d'un voyage : upload direct depuis le téléphone (appareil photo / galerie) ou URL.
 * Photo principale + galerie. Les fichiers partent sur le stockage cloud, seule l'URL est gardée.
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const uploadOne = async (file, getAuthConfig) => {
  const fd = new FormData();
  fd.append('file', file);
  const r = await axios.post(`${API}/admin/trips/upload-image`, fd, { ...getAuthConfig(), timeout: 120000 });
  return r.data.url;
};

const UploadButton = ({ label, multiple, onFiles, busy, testId }) => {
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" accept="image/*" multiple={multiple} className="hidden"
        onChange={e => { const files = Array.from(e.target.files || []); e.target.value = ''; if (files.length) onFiles(files); }}
        data-testid={`${testId}-input`} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy}
        className="text-xs bg-[#1E3A5F] text-white px-3 py-1.5 rounded-lg font-semibold disabled:opacity-50" data-testid={testId}>
        {busy ? '⏳ Envoi…' : label}
      </button>
    </>
  );
};

const FocusPicker = ({ url, focus, onFocus }) => {
  const f = focus || { x: 50, y: 50 };
  const pick = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    onFocus({ x: Math.round(((e.clientX - r.left) / r.width) * 100), y: Math.round(((e.clientY - r.top) / r.height) * 100) });
  };
  return (
    <div className="flex items-start gap-3 flex-wrap" data-testid="focus-picker">
      <div className="relative w-64 cursor-crosshair select-none" onClick={pick} title="Clique sur le point le plus important de la photo" data-testid="focus-picker-image">
        <img src={url} alt="" className="w-64 rounded-lg border block" draggable={false} />
        <div className="absolute w-6 h-6 -ml-3 -mt-3 rounded-full border-2 border-white shadow ring-2 ring-[#5BC5D4] pointer-events-none" style={{ left: `${f.x}%`, top: `${f.y}%` }} />
      </div>
      <div className="space-y-1">
        <p className="text-[11px] text-gray-500 w-48">Aperçu carte (comme sur le site) :</p>
        <div className="w-48 h-28 overflow-hidden rounded-lg border">
          <img src={url} alt="" className="w-full h-full object-cover" style={{ objectPosition: `${f.x}% ${f.y}%` }} data-testid="focus-preview-card" />
        </div>
        <div className="w-48 h-16 overflow-hidden rounded-lg border">
          <img src={url} alt="" className="w-full h-full object-cover" style={{ objectPosition: `${f.x}% ${f.y}%` }} data-testid="focus-preview-hero" />
        </div>
        <button type="button" onClick={() => onFocus(null)} className="text-[11px] text-gray-500 underline" data-testid="focus-reset">Recentrer</button>
      </div>
    </div>
  );
};

export const TripPhotosEditor = ({ imageUrl, gallery, onImageUrl, onGallery, getAuthConfig, focus, onFocus }) => {
  const [busy, setBusy] = useState('');
  const [progress, setProgress] = useState('');
  const list = gallery || [];

  const sendMain = async ([file]) => {
    setBusy('main');
    try { onImageUrl(await uploadOne(file, getAuthConfig)); }
    catch (e) { alert('Erreur upload : ' + (e.response?.data?.detail || e.message)); }
    setBusy('');
  };

  const sendGallery = async (files) => {
    setBusy('gallery');
    const urls = [];
    for (let i = 0; i < files.length; i++) {
      setProgress(`${i + 1}/${files.length}`);
      try { urls.push(await uploadOne(files[i], getAuthConfig)); }
      catch (e) { alert(`Photo ${i + 1} : ` + (e.response?.data?.detail || e.message)); }
    }
    onGallery([...list, ...urls]);
    setBusy(''); setProgress('');
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list]; [next[i], next[j]] = [next[j], next[i]];
    onGallery(next);
  };

  return (
    <div className="md:col-span-2 border-t pt-3 space-y-3" data-testid="trip-photos-editor">
      <div>
        <p className="text-sm font-bold text-[#1E3A5F]">📷 Photo principale</p>
        <p className="text-[11px] text-gray-500 mb-2">Depuis ton téléphone (appareil photo ou galerie) — la photo est redimensionnée automatiquement.</p>
        <div className="flex items-start gap-3 flex-wrap">
          {imageUrl ? <img src={imageUrl} alt="" className="w-40 h-24 object-cover rounded-lg border" data-testid="trip-main-photo-preview" />
            : <div className="w-40 h-24 rounded-lg border-2 border-dashed flex items-center justify-center text-xs text-gray-400">Aucune photo</div>}
          <div className="flex flex-col gap-2">
            <UploadButton label="📷 Choisir / prendre une photo" onFiles={sendMain} busy={busy === 'main'} testId="upload-main-photo-btn" />
            <input type="text" value={imageUrl || ''} onChange={e => onImageUrl(e.target.value)} placeholder="…ou coller une URL"
              className="px-2 py-1 border rounded text-xs w-72" data-testid="trip-form-image-url" />
          </div>
        </div>
      </div>
      {imageUrl && onFocus && (
        <div>
          <p className="text-sm font-bold text-[#1E3A5F]">🎯 Zone visible (recadrage)</p>
          <p className="text-[11px] text-gray-500 mb-2">Clique sur le visage ou le monument à garder au centre : la carte et la grande image du site se recadrent dessus.</p>
          <FocusPicker url={imageUrl} focus={focus} onFocus={onFocus} />
        </div>
      )}
      <div>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div>
            <p className="text-sm font-bold text-[#1E3A5F]">🖼 Galerie ({list.length})</p>
            <p className="text-[11px] text-gray-500">Plusieurs photos à la fois. ◀ ▶ pour changer l'ordre, ✕ pour retirer.</p>
          </div>
          <UploadButton label={`➕ Ajouter des photos${progress ? ` (${progress})` : ''}`} multiple onFiles={sendGallery} busy={busy === 'gallery'} testId="upload-gallery-btn" />
        </div>
        <div className="flex flex-wrap gap-2">
          {list.map((u, i) => (
            <div key={`${u}-${i}`} className="relative group" data-testid={`gallery-item-${i}`}>
              <img src={u} alt="" className="w-28 h-20 object-cover rounded-lg border" />
              <div className="absolute inset-x-0 bottom-0 flex justify-between px-1 pb-1 opacity-0 group-hover:opacity-100 transition">
                <button type="button" onClick={() => move(i, -1)} className="bg-white/90 rounded px-1 text-[10px] font-bold">◀</button>
                <button type="button" onClick={() => onGallery(list.filter((_, j) => j !== i))} className="bg-red-600 text-white rounded px-1 text-[10px] font-bold" data-testid={`gallery-remove-${i}`}>✕</button>
                <button type="button" onClick={() => move(i, 1)} className="bg-white/90 rounded px-1 text-[10px] font-bold">▶</button>
              </div>
            </div>
          ))}
        </div>
        <textarea value={list.join('\n')} onChange={e => onGallery(e.target.value.split('\n'))}
          onBlur={e => onGallery(e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
          className="mt-2 w-full px-2 py-1 border rounded text-[11px] text-gray-500" rows={2} placeholder="…ou coller des URL (1 par ligne)" data-testid="trip-form-gallery" />
      </div>
    </div>
  );
};
